import { WorkflowStepDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Input } from "@babackapp/ui/components/input";
import { Button } from "@babackapp/ui/components/button";
import { WorkflowInputText } from "./workflow-text-input";
import { useModuleFlowConstants } from "../hooks/use-module-flow-constants";
import { useWorkflowModulesForms } from "../hooks/use-workflow-module-forms";
import UserPicker from "@/components-legacy/userPicker";
import Select from "./select";
import Tooltip from "@/components-legacy/tooltip";
import TagPicker from "@/components-legacy/tagPicker";

export default function ModuleFlow(props: {
  flowStep: WorkflowStepDto;
  shopId: string;
  onSaveAction: (flowStep: WorkflowStepDto) => void;
  onSaveTrigger: (flowStep: WorkflowStepDto) => void;
  onCancel: () => void;
  onDelete: () => void;
}) {
  const t = useTranslation();
  const {
    flowStep,
    workFlowInputOnChangeTitle,
    extractWorkflowMailBody,
    workflowInputtextOnChangeArea,
    onSaveUpdates,
    setFlowStep,
    onUpdateTags,
  } = useWorkflowModulesForms({
    currentFlowStep: props.flowStep,
    onSaveAction: props.onSaveAction,
    onSaveTrigger: props.onSaveTrigger,
  });

  const {
    validFlowOperations,
    flowActionsOptions,
    triggerSubActionsOptions,
    actionsUpdateReturnRequestOptions,
  } = useModuleFlowConstants();

  const savingIsDisabled = (flowStep: WorkflowStepDto) => {
    const conditionallyValidOperations = [
      {
        operation: "returnRequest:assignee:update",
        condition:
          flowStep.parameters.users && flowStep.parameters.users.length > 0,
      },
      {
        operation: "returnRequest:tag:shopify",
        condition: !!flowStep.parameters.tags,
      },
      {
        operation: "returnRequest:additionnalDetails:shopify",
        condition: !!flowStep.parameters.additionnalNote,
      },
      {
        operation: "mail:ticket:zendesk",
        condition: !!flowStep.parameters.mail?.body.length && !!flowStep.parameters.mail?.subject.length,
      },
      {
        operation: "mail:ticket:gorgias",
        condition: !!flowStep.parameters.mail?.body.length && !!flowStep.parameters.mail?.subject.length,
      },
      {
        operation: "returnRequest:note:shopify",
        condition: !!flowStep.parameters.note,
      },
    ];

    const isConditionallyValid = conditionallyValidOperations.some(
      (op) => flowStep.operation === op.operation && op.condition,
    );

    return (
      !(
        validFlowOperations.includes(flowStep.operation) || isConditionallyValid
      ) || props.flowStep === flowStep
    );
  };

  const tags: string[] = [];

  return (
    <div className="h-full bg-white shadow-md p-4 divide-y space-y-4">
      <div className="space-y-3">
        <>
          {flowStep.type !== "TRIGGER" ? (
            <>
              <div className="text-md">{t("workflow.module.flowAction")}</div>
              <div className="space-y-3">
                <div className="text-sm">{t("workflow.module.subAction")}</div>
                <Select
                  className="w-full"
                  onChange={(operation: string) => {
                    setFlowStep({
                      ...flowStep,
                      operation,
                    });
                  }}
                  options={triggerSubActionsOptions}
                  value={flowStep.operation}
                />
              </div>
            </>
          ) : (
            <>
              <div className="text-md">{t("workflow.module.trigger")}</div>
              <div className="space-y-3">
                <div className="text-sm">{t("workflow.module.when")}</div>
                <Select
                  className="w-full"
                  onChange={() => {
                    setFlowStep({
                      ...flowStep,
                      ressource: "returnRequest",
                    });
                  }}
                  options={[
                    {
                      key: "returnRequest",
                      label: t("workflow.module.returnRequest"),
                    },
                  ]}
                  value={flowStep.ressource}
                />
              </div>
              <div className="space-y-3">
                <div className="text-sm">{t("workflow.module.is")}</div>
                <Select
                  className="w-full"
                  onChange={(operation: string) => {
                    setFlowStep({
                      ...flowStep,
                      operation,
                    });
                  }}
                  options={flowActionsOptions}
                  value={flowStep.operation}
                />
              </div>
            </>
          )}
          {flowStep.operation == "mail:ticket:zendesk" && (
            <div className="text-sm">
              <WorkflowInputText
                title={flowStep.parameters.mail?.subject || ""}
                onChangeCustom={(value) => {
                  setFlowStep({
                    ...flowStep,
                    parameters: {
                      ...flowStep.parameters,
                      customFields: value,
                    },
                  });
                }}
                onChangeTitle={workFlowInputOnChangeTitle}
                customFields={flowStep.parameters.customFields}
                textArea={extractWorkflowMailBody()}
                onChangeArea={workflowInputtextOnChangeArea}
              />
            </div>
          )}
          {flowStep.operation == "mail:ticket:gorgias" && (
            <div className="text-sm">
              <WorkflowInputText
                title={flowStep.parameters.mail?.subject || ""}
                onChangeCustom={(value) => {
                  setFlowStep({
                    ...flowStep,
                    parameters: {
                      ...flowStep.parameters,
                      customFields: value,
                    },
                  });
                }}
                customFields={flowStep.parameters.customFields}
                onChangeTitle={workFlowInputOnChangeTitle}
                textArea={extractWorkflowMailBody()}
                onChangeArea={workflowInputtextOnChangeArea}
              />
            </div>
          )}
          {flowStep.operation == "returnRequest:assignee:update" && (
            <div className="flex">
              <UserPicker
                shopId={props.shopId}
                usersPicked={
                  flowStep.parameters.users ? flowStep.parameters.users : []
                }
                onUpdateUser={(userId: string) => {
                  const step: WorkflowStepDto = { ...flowStep };
                  step.parameters.tags = null;

                  if (
                    flowStep.parameters.users &&
                    flowStep.parameters.users.includes(userId)
                  ) {
                    step.parameters.users = step.parameters.users?.filter(
                      (user) => user != userId,
                    );
                    setFlowStep(step);
                  } else {
                    if (step.parameters.users) {
                      step.parameters.users.push(userId);
                    } else {
                      step.parameters.users = [userId];
                    }
                    setFlowStep(step);
                  }
                }}
              />
            </div>
          )}
          {flowStep.operation == "returnRequest:tag:shopify" && (
            <>
              <Input
                value={flowStep.parameters.tags || ""}
                onChange={(event) => {
                  const step = { ...flowStep };
                  step.parameters.tags = event.target.value;
                  setFlowStep(step);
                }}
              />
              <Tooltip
                description={""}
                descriptionReact={
                  <div className="text-sm p-4 bg-white">
                    <div>{t("workflow.module.tooltipsTagExp")}</div>
                  </div>
                }
                className="text-xs text-gray-400"
              >
                <>{t("workflow.module.tooltipsTag")}</>
              </Tooltip>
            </>
          )}
          {flowStep.operation == "returnRequest:note:shopify" && (
            <div className="text-sm">
              <WorkflowInputText
                title={flowStep.parameters.note || ""}
                onChangeTitle={(value) => {
                  const step = { ...flowStep };
                  step.parameters.note = value;
                  setFlowStep(step);
                }}
              />
            </div>
          )}
          {flowStep.operation == "returnRequest:status:update" && (
            <Select
              className="w-full"
              onChange={(status: string) => {
                const step = { ...flowStep };
                step.parameters.status = [status];
                setFlowStep(step);
              }}
              options={actionsUpdateReturnRequestOptions}
              value={flowStep.parameters.status?.[0] || "created"}
            />
          )}
          {flowStep.operation == "returnRequest:additionnalDetails:shopify" && (
            <div className="text-sm">
              <WorkflowInputText
                title={flowStep.parameters.additionnalNote?.name || ""}
                onChangeTitle={(value) => {
                  const step = { ...flowStep };

                  step.parameters.additionnalNote = {
                    name: value,
                    value: step.parameters.additionnalNote?.value
                      ? step.parameters.additionnalNote?.value
                      : "",
                  };
                  setFlowStep(step);
                }}
                textArea={flowStep.parameters.additionnalNote?.value || ""}
                onChangeArea={(value) => {
                  const step = { ...flowStep };

                  step.parameters.additionnalNote = {
                    name: step.parameters.additionnalNote?.name
                      ? step.parameters.additionnalNote?.name
                      : "",
                    value: value,
                  };

                  setFlowStep(step);
                }}
              />
            </div>
          )}

          {flowStep.operation == "returnRequest:tags:update" && (
            <div>
              <TagPicker
                tags={tags}
                onUpdateTag={onUpdateTags}
                tagsPicked={
                  // Todo: fix this
                  [flowStep.parameters.tags || ""]
                }
              />
            </div>
          )}
        </>
      </div>
      <div>
        <div className="flex justify-end items-center space-x-1 mt-4">
          <div
            className={`${
              props.flowStep === flowStep ? "invisible" : "visible"
            }`}
          >
            <Button size="lg" onClick={props.onCancel} type="button">
              {t("misc.cancel")}
            </Button>
          </div>
          <Button
            type="button"
            variant={"default"}
            size="lg"
            disabled={savingIsDisabled(flowStep)}
            className="w-24"
            onClick={() => onSaveUpdates(flowStep.type)}
          >
            {t("misc.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
